import { FC, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@mui/material';
import { endOfDay, startOfDay } from 'date-fns';
import ClickOpenDateTimePicker from './ClickOpenDateTimePicker';

/**
 * Shared "Equipment Needed" multiselect + per-unit occupied-window editor,
 * extracted from AddAppointmentDialog / ScheduleBarnVisitDialog (the two
 * copies had already drifted in helper text).
 *
 * Each selected unit reserves a window; "All day" replaces the explicit
 * start/end with the WHOLE appointment day. Nothing is stored for all-day
 * except the boolean — the actual window is computed at submit from the
 * current appointment date (buildEquipmentPayload), so changing the
 * appointment date afterwards can never leave a stale all-day window.
 *
 * Equipment is vet-exclusive for its window (server 409s a conflicting vet's
 * reservation); an all-day reservation therefore blocks other vets for the
 * whole day — that's the point of the toggle.
 */

export interface EquipmentAvailabilityItem {
  id: string;
  name: string;
  type: string;
  available: boolean;
}

export interface EquipmentWindow {
  startTime: Date;
  endTime: Date;
}

/**
 * The wire shape POST /appointments(+/barn-visit) expects. Computed at
 * submit time: all-day items span the appointment's calendar day (browser
 * TZ — the same clock every other datetime in the dialog uses).
 */
export function buildEquipmentPayload(
  selectedIds: string[],
  windows: Record<string, EquipmentWindow>,
  allDayById: Record<string, boolean>,
  appointmentStart: Date,
  appointmentEnd: Date,
): Array<{ id: string; startTime: string; endTime: string }> | undefined {
  if (selectedIds.length === 0) return undefined;
  return selectedIds.map((id) => {
    if (allDayById[id]) {
      return {
        id,
        startTime: startOfDay(appointmentStart).toISOString(),
        endTime: endOfDay(appointmentStart).toISOString(),
      };
    }
    return {
      id,
      startTime: (windows[id]?.startTime ?? appointmentStart).toISOString(),
      endTime: (windows[id]?.endTime ?? appointmentEnd).toISOString(),
    };
  });
}

/**
 * True iff [start, end] spans exactly the whole calendar day of `start` — the
 * shape buildEquipmentPayload emits for all-day. Used to re-infer the All-day
 * checkbox when an existing reservation is opened for editing. Local-clock
 * comparison: a reservation created in another timezone simply re-opens as an
 * explicit window instead (graceful, never corrupting).
 */
export const isAllDayWindow = (start: Date, end: Date): boolean =>
  start.getTime() === startOfDay(start).getTime() &&
  end.getTime() === endOfDay(start).getTime();

interface EquipmentWindowsPickerProps {
  items: EquipmentAvailabilityItem[];
  selectedIds: string[];
  onSelectedIdsChange: (ids: string[]) => void;
  windows: Record<string, EquipmentWindow>;
  onWindowsChange: (windows: Record<string, EquipmentWindow>) => void;
  allDayById: Record<string, boolean>;
  onAllDayChange: (byId: Record<string, boolean>) => void;
  disabled?: boolean;
  /** Shown under the select (offline notice, vet requirement, …). */
  helperText?: string;
  /** Defaults for a newly selected unit's window. */
  appointmentStart: Date | null;
  appointmentEnd: Date | null;
}

const EquipmentWindowsPicker: FC<EquipmentWindowsPickerProps> = ({
  items,
  selectedIds,
  onSelectedIdsChange,
  windows,
  onWindowsChange,
  allDayById,
  onAllDayChange,
  disabled = false,
  helperText,
  appointmentStart,
  appointmentEnd,
}) => {
  const [selectOpen, setSelectOpen] = useState(false);

  const handleSelectionChange = (selected: string[]) => {
    onSelectedIdsChange(selected);
    setSelectOpen(false);
    // For newly added items, default window = appointment start → end.
    const nextWindows = { ...windows };
    selected.forEach((id) => {
      if (!nextWindows[id]) {
        const start = appointmentStart ?? new Date();
        const end = appointmentEnd ?? new Date(start.getTime() + 60 * 60_000);
        nextWindows[id] = { startTime: start, endTime: end };
      }
    });
    // Remove windows + all-day flags for deselected items.
    const nextAllDay = { ...allDayById };
    Object.keys(nextWindows).forEach((id) => {
      if (!selected.includes(id)) delete nextWindows[id];
    });
    Object.keys(nextAllDay).forEach((id) => {
      if (!selected.includes(id)) delete nextAllDay[id];
    });
    onWindowsChange(nextWindows);
    onAllDayChange(nextAllDay);
  };

  return (
    <>
      <FormControl fullWidth disabled={disabled}>
        <InputLabel>Equipment Needed (optional)</InputLabel>
        <Select
          multiple
          value={selectedIds}
          open={selectOpen}
          onOpen={() => setSelectOpen(true)}
          onClose={() => setSelectOpen(false)}
          onChange={(e) => handleSelectionChange(e.target.value as string[])}
          input={<OutlinedInput label="Equipment Needed (optional)" />}
          renderValue={(selected) =>
            (selected as string[])
              .map((id) => items.find((eq) => eq.id === id)?.name ?? id)
              .join(', ')
          }
        >
          {items.length === 0 ? (
            <MenuItem disabled>
              <em>No equipment configured — add units on the Equipment page</em>
            </MenuItem>
          ) : (
            items.map((eq) => {
              // Equipment is vet-exclusive for its window: a unit another
              // provider holds for an overlapping time is blocked. Keep an
              // already-picked unit selectable so it can still be removed
              // even if availability flips.
              const blocked = !eq.available && !selectedIds.includes(eq.id);
              return (
                <MenuItem key={eq.id} value={eq.id} disabled={blocked}>
                  <Checkbox checked={selectedIds.includes(eq.id)} />
                  <ListItemText
                    primary={eq.name}
                    secondary={
                      blocked
                        ? 'Reserved by another provider for this time'
                        : eq.type !== 'other'
                          ? eq.type
                          : undefined
                    }
                  />
                </MenuItem>
              );
            })
          )}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>

      {/* Per-item occupied window — appears only when equipment is selected */}
      {selectedIds.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {selectedIds.map((id) => {
            const eq = items.find((e) => e.id === id);
            const win = windows[id];
            if (!eq || !win) return null;
            const allDay = !!allDayById[id];
            return (
              <Box
                key={id}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {eq.name} — Occupied Window
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={allDay}
                        onChange={(e) =>
                          onAllDayChange({ ...allDayById, [id]: e.target.checked })
                        }
                      />
                    }
                    label={<Typography variant="body2">All day</Typography>}
                    sx={{ mr: 0 }}
                  />
                </Box>
                {allDay ? (
                  <Typography variant="caption" color="text.secondary">
                    Reserved 12:00 AM – 11:59 PM on the appointment date — no other
                    provider can book this unit that day.
                  </Typography>
                ) : (
                  <>
                    <ClickOpenDateTimePicker
                      label="Start Time"
                      size="small"
                      value={win.startTime}
                      onChange={(v) =>
                        v &&
                        onWindowsChange({
                          ...windows,
                          [id]: { ...windows[id], startTime: v },
                        })
                      }
                    />
                    <ClickOpenDateTimePicker
                      label="End Time"
                      size="small"
                      value={win.endTime}
                      onChange={(v) =>
                        v &&
                        onWindowsChange({
                          ...windows,
                          [id]: { ...windows[id], endTime: v },
                        })
                      }
                    />
                  </>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );
};

export default EquipmentWindowsPicker;
